import type { ReactNode } from 'react';
import { TILE_VALUES } from '@scrabble-daily/engine';
import type { TileDto } from '../../api/puzzles';

interface RackProps {
  tiles: TileDto[];
  selectedTileId: string | null;
  onTileTap?: (tile: TileDto) => void;
  /** Lets the game screen wrap each tile in a drag source without coupling Rack to react-dnd. */
  tileWrapper?: (tile: TileDto, button: ReactNode) => ReactNode;
  /** Wraps every slot (tiles and empty recesses) — used for drop-to-reorder targets. */
  slotWrapper?: (index: number, tile: TileDto | null, node: ReactNode) => ReactNode;
}

/** 7-slot rack; empty slots render as recesses so the row never collapses. */
export function Rack({ tiles, selectedTileId, onTileTap, tileWrapper, slotWrapper }: RackProps) {
  const slots: Array<TileDto | null> = [
    ...tiles,
    ...Array.from({ length: Math.max(0, 7 - tiles.length) }, () => null),
  ];

  return (
    <div role="toolbar" aria-label="Tile rack" className="flex justify-center gap-1.5">
      {slots.map((tile, i) => {
        const bare = !tileWrapper && !slotWrapper;
        let node: ReactNode;
        if (!tile) {
          node = (
            <div
              key={bare ? `empty-${i}` : undefined}
              aria-hidden="true"
              className="h-11 w-10 rounded-md border border-dashed border-stone-300 bg-stone-100"
            />
          );
        } else {
          const button = (
            <button
              key={bare ? tile.tile_id : undefined}
              type="button"
              data-testid={`rack-tile-${tile.tile_id}`}
              aria-label={
                tile.is_blank
                  ? 'Blank tile (0 points)'
                  : `${tile.letter} (${TILE_VALUES[tile.letter] ?? 0} points)`
              }
              aria-pressed={selectedTileId === tile.tile_id}
              onClick={() => onTileTap?.(tile)}
              className={`flex h-11 w-10 flex-col items-center justify-center rounded-md border-b-4 border-tile-edge bg-tile-face text-lg font-bold text-stone-900 shadow-sm active:translate-y-px ${
                selectedTileId === tile.tile_id ? 'ring-2 ring-emerald-600' : ''
              }`}
            >
              {tile.is_blank ? (
                <span className="text-stone-400">?</span>
              ) : (
                <>
                  {tile.letter}
                  <span className="text-[9px] font-medium leading-none">
                    {TILE_VALUES[tile.letter] ?? 0}
                  </span>
                </>
              )}
            </button>
          );
          node = tileWrapper ? tileWrapper(tile, button) : button;
        }
        if (bare) return node;
        return (
          <div key={tile?.tile_id ?? `empty-${i}`} className="contents">
            {slotWrapper ? slotWrapper(i, tile, node) : node}
          </div>
        );
      })}
    </div>
  );
}
